import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Input, Spin, Tooltip } from 'antd'
import {
  CloseOutlined,
  ColumnHeightOutlined,
  CopyOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'
import { Button, toast } from '@crosscode/gravity-components'
import { api } from '@/api/client'
import styles from './IntegrationLogsModal.module.css'

export type IntegrationLogLine = {
  timestamp?: string
  level?: string
  logger?: string
  message?: string
  raw?: string
}

type Props = {
  open: boolean
  title?: string
  logsUrl: string
  onClose: () => void
}

type Entry = IntegrationLogLine & { id: string }

const LEVEL_CHIPS = [
  { value: '', label: 'Все', tone: '' as const },
  { value: 'ERROR', label: 'Err', tone: 'err' as const },
  { value: 'WARN', label: 'Warn', tone: 'warn' as const },
  { value: 'INFO', label: 'Info', tone: '' as const },
  { value: 'DEBUG', label: 'Dbg', tone: '' as const },
] as const

function levelTone(level?: string | null): 'err' | 'warn' | 'info' | '' {
  const l = (level || '').toUpperCase()
  if (l === 'FATAL' || l === 'ERROR') return 'err'
  if (l === 'WARN' || l === 'WARNING') return 'warn'
  if (l === 'INFO') return 'info'
  return ''
}

function formatLevel(level?: string | null) {
  return (level || '—').toUpperCase().replace('WARNING', 'WARN')
}

function normalizeLevel(level?: string | null) {
  return (level || '').toUpperCase().replace('WARNING', 'WARN')
}

function highlightMessage(text: string, q: string) {
  if (!q.trim()) return text
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx < 0) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className={styles.mark}>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  )
}

function entryText(line: IntegrationLogLine) {
  if (line.raw) return line.raw
  const parts = [line.timestamp, line.level, line.logger, line.message].filter(Boolean)
  return parts.join(' ')
}

export function IntegrationLogsModal({ open, title, logsUrl, onClose }: Props) {
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bootRef = useRef(0)

  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(false)
  const [level, setLevel] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [wrap, setWrap] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const load = useCallback(async () => {
    const boot = ++bootRef.current
    setLoading(true)
    try {
      const data = await api<{ logs?: IntegrationLogLine[] } | IntegrationLogLine[]>(logsUrl)
      if (boot !== bootRef.current) return
      // API отдаёт файл сверху вниз (старое → новое); показываем свежие сверху
      const list = (Array.isArray(data) ? data : data.logs || []).slice().reverse()
      setEntries(
        list.map((line, i) => ({
          ...line,
          id: String(i),
        })),
      )
      setSelectedId(null)
    } catch (e) {
      if (boot !== bootRef.current) return
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить логи')
      setEntries([])
    } finally {
      if (boot === bootRef.current) setLoading(false)
    }
  }, [logsUrl])

  useEffect(() => {
    if (!open) return
    void load()
    return () => {
      bootRef.current += 1
    }
  }, [open, load])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const counts = useMemo(() => {
    let errors = 0
    let warnings = 0
    let info = 0
    let debug = 0
    for (const e of entries) {
      const l = normalizeLevel(e.level)
      if (l === 'ERROR' || l === 'FATAL') errors += 1
      else if (l === 'WARN') warnings += 1
      else if (l === 'INFO') info += 1
      else if (l === 'DEBUG' || l === 'TRACE') debug += 1
    }
    return { total: entries.length, errors, warnings, info, debug }
  }, [entries])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return entries.filter((e) => {
      if (level) {
        const l = normalizeLevel(e.level)
        if (level === 'ERROR' && l !== 'ERROR' && l !== 'FATAL') return false
        if (level === 'WARN' && l !== 'WARN') return false
        if (level === 'INFO' && l !== 'INFO') return false
        if (level === 'DEBUG' && l !== 'DEBUG' && l !== 'TRACE') return false
      }
      if (!q) return true
      return entryText(e).toLowerCase().includes(q)
    })
  }, [entries, level, search])

  const selected = useMemo(
    () => (selectedId == null ? null : entries.find((e) => e.id === selectedId) || null),
    [entries, selectedId],
  )

  const onSearchChange = (value: string) => {
    setSearchInput(value)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => setSearch(value), 200)
  }

  const copySelected = async () => {
    if (!selected) {
      toast.error('Выберите строку')
      return
    }
    try {
      await navigator.clipboard.writeText(entryText(selected))
      toast.success('Скопировано')
    } catch {
      toast.error('Не удалось скопировать')
    }
  }

  if (!open) return null

  return createPortal(
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="presentation"
    >
      <div className={styles.shell} role="dialog" aria-modal="true" aria-label="Логи модуля интеграции">
        <header className={styles.header}>
          <div className={styles.headerMain}>
            <h2 className={styles.headerTitle}>Логи · {title || 'Модуль интеграции'}</h2>
            <div className={styles.headerMeta}>
              application.log · {counts.total.toLocaleString('ru-RU')} строк
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Закрыть">
            <CloseOutlined />
          </button>
        </header>

        <div className={styles.command}>
          <div className={`${styles.searchWrap} ${styles.searchInput}`}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Поиск по логам…"
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          <div className={styles.chips}>
            {LEVEL_CHIPS.map((chip) => {
              const count =
                chip.value === ''
                  ? counts.total
                  : chip.value === 'ERROR'
                    ? counts.errors
                    : chip.value === 'WARN'
                      ? counts.warnings
                      : chip.value === 'INFO'
                        ? counts.info
                        : counts.debug
              const active = level === chip.value
              return (
                <button
                  key={chip.value || 'all'}
                  type="button"
                  className={[
                    styles.chip,
                    active ? styles.chipActive : '',
                    chip.tone === 'err' ? styles.chipErr : '',
                    chip.tone === 'warn' ? styles.chipWarn : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setLevel(chip.value)}
                >
                  {chip.label}
                  <span className={styles.chipCount}>{count}</span>
                </button>
              )
            })}
          </div>

          <div className={styles.actions}>
            <Tooltip title={wrap ? 'Перенос строк вкл.' : 'Перенос строк выкл.'}>
              <button
                type="button"
                className={[styles.iconToggle, wrap ? styles.iconToggleOn : ''].filter(Boolean).join(' ')}
                onClick={() => setWrap((v) => !v)}
                aria-label="Перенос строк"
              >
                <ColumnHeightOutlined />
              </button>
            </Tooltip>
            <Button size="small" icon={<CopyOutlined />} onClick={() => void copySelected()} disabled={!selected}>
              Копировать
            </Button>
            <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>
              Обновить
            </Button>
          </div>
        </div>

        <div className={styles.body}>
          {loading && entries.length === 0 ? (
            <div className={styles.empty}>
              <Spin />
              <div className={styles.emptyTitle}>Загрузка логов…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyTitle}>
                {entries.length === 0 ? 'Логи не найдены' : 'Нет строк по фильтру'}
              </div>
            </div>
          ) : (
            <Virtuoso
              ref={virtuosoRef}
              className={`${styles.viewer} ${wrap ? styles.wrap : styles.nowrap}`}
              data={filtered}
              followOutput={false}
              itemContent={(_index, line) => {
                const tone = levelTone(line.level)
                const selectedCls = selectedId === line.id ? styles.entrySelected : ''
                const toneCls =
                  tone === 'err' ? styles.entryErr : tone === 'warn' ? styles.entryWarn : ''
                const lvlCls =
                  tone === 'err' ? styles.lvlErr : tone === 'warn' ? styles.lvlWarn : tone === 'info' ? styles.lvlInfo : ''
                const message = line.message || line.raw || '—'
                return (
                  <div
                    className={[styles.entry, selectedCls, toneCls].filter(Boolean).join(' ')}
                    onClick={() => setSelectedId(line.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setSelectedId(line.id)
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <span className={styles.gutter} />
                    <span className={styles.ts}>{line.timestamp || '—'}</span>
                    <span className={[styles.lvl, lvlCls].filter(Boolean).join(' ')}>
                      {formatLevel(line.level)}
                    </span>
                    <span className={styles.mod} title={line.logger || undefined}>
                      {line.logger || '—'}
                    </span>
                    <span className={styles.msg}>{highlightMessage(message, search)}</span>
                  </div>
                )
              }}
            />
          )}
        </div>

        <div className={styles.listFooter}>
          <span className={styles.footerHint}>
            Показано {filtered.length.toLocaleString('ru-RU')} из {counts.total.toLocaleString('ru-RU')}
            {selected ? ' · строка выбрана' : ''}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  )
}
